/**
 * Token integrity guard — PR5 hardening.
 *
 * Canonical forbidden hex list for the deprecated Teal/Graphite palette.
 * Kept in a dedicated module (separate from `tokens.ts`) so that:
 *   1. The palette file stays a single-source-of-truth for runtime
 *      colors, without ever containing a forbidden hex literal.
 *   2. Source-level regex guards on `tokens.ts` (see
 *      `__tests__/runtime-shell-convergence.test.ts`) remain trivially
 *      correct.
 *
 * Authority:
 *   - contracts/design/MH_DESIGN_V4_FOG.md
 */

// Canonical list of hexes that MUST never appear inside runtime tokens.
// Any future forbidden hex should be added here — the hex literals live
// ONLY in this module.
export const FORBIDDEN_TOKEN_HEXES = [
  '#14B8A6',
  '#0D9488',
  '#2DD4BF',
] as const;

export type ForbiddenTokenHex = (typeof FORBIDDEN_TOKEN_HEXES)[number];

/**
 * Compile-time assertion: if any token value string matches a forbidden
 * hex, this type resolves to `false` and the `_tokenIntegrityOk`
 * constant below fails to type-check.
 */
export type AssertNoForbiddenTokens<V extends string> =
  Extract<V, ForbiddenTokenHex> extends never ? true : false;

/**
 * Dev-only runtime guard. In `NODE_ENV !== 'production'` we emit a loud
 * console warning if a forbidden hex is detected in a token map. The
 * call is a no-op in production to avoid spamming end users — the
 * compile-time assertion above is the real gate for CI.
 */
export function assertNoForbiddenTokenHexes(
  tokenMap: Readonly<Record<string, unknown>>,
): void {
  if (typeof process === 'undefined') return;
  if (process.env?.NODE_ENV === 'production') return;

  const offending = Object.entries(tokenMap).filter(
    ([, value]) =>
      typeof value === 'string' &&
      (FORBIDDEN_TOKEN_HEXES as readonly string[]).some((hex) =>
        (value as string).toUpperCase().includes(hex),
      ),
  );
  if (offending.length === 0) return;

  // eslint-disable-next-line no-console
  console.warn(
    '[design-tokens] FORBIDDEN PALETTE DETECTED (Teal/Graphite hex). ' +
      'Runtime palette must be Fog Oyster + Espresso. Offending keys: ' +
      offending.map(([k]) => k).join(', '),
  );
}
